<!-- src/components/device/ContactsPanel.vue -->
<!--
  Les contacts d'alerte d'un appareil : qui est appelé, qui reçoit un SMS, et pour quoi.

  Composant partagé entre l'espace admin et l'espace client, comme l'est déjà
  `DeviceSchedule`. L'écran client ne savait qu'AFFICHER la liste — aucun bouton, alors que
  le backend l'autorisait depuis toujours (les quatre routes sont en `hasDeviceAccess`, pas
  en `isAdmin`). Recopier le formulaire de l'admin aurait créé une seconde version qui aurait
  dérivé de la première à la première correction.

  C'est le client, et lui seul, qui sait qui doit être prévenu quand sa sirène se déclenche.
-->
<template>
  <div class="bg-white rounded-lg shadow overflow-hidden">
    <div class="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
      <h3 class="font-semibold text-gray-800">{{ $t('contacts.titre', { nombre: contacts.length }) }}</h3>
      <button v-if="modifiable" @click="ouvrirAjout" class="btn btn-primary text-sm">{{ $t('contacts.ajouter') }}</button>
    </div>

    <!-- Un appareil sans contact ne prévient personne. C'est le seul état de cette page qui
         mérite un avertissement, pas une phrase neutre : l'installation a l'air de
         fonctionner, et personne ne recevra rien. -->
    <div v-if="contacts.length === 0" class="p-8 text-center">
      <p class="text-sm font-medium text-amber-700">{{ $t('contacts.aucunContact') }}</p>
      <p class="mt-1 text-sm text-gray-500">
        {{ $t('contacts.aucunContactDetail') }}
      </p>
    </div>

    <div v-else class="divide-y divide-gray-100">
      <div v-for="contact in contacts" :key="contact.id" class="px-4 py-4 hover:bg-gray-50">
        <div class="flex justify-between items-start gap-4">
          <div>
            <div class="font-medium text-gray-900">{{ contact.name }}</div>
            <div class="text-sm text-gray-500">{{ contact.phone_number }}
              <!-- Langue des SMS et appels de ce contact (absente = celle du titulaire). -->
              <span v-if="contact.lang" class="ml-1 text-xs text-gray-400">· {{ nomLangue(contact.lang) }}</span>
            </div>
            <div class="flex flex-wrap gap-1 mt-1">
              <span v-if="contact.call_on_alarm"      class="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded">{{ $t('contacts.appelAlarme') }}</span>
              <span v-if="contact.sms_on_alarm"       class="text-xs px-1.5 py-0.5 bg-red-50 text-red-500 rounded">{{ $t('contacts.smsAlarme') }}</span>
              <span v-if="contact.sms_on_tamper"      class="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded">{{ $t('contacts.smsSabotage') }}</span>
              <span v-if="contact.sms_on_power_loss"  class="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-600 rounded">{{ $t('contacts.smsCoupureSecteur') }}</span>
              <span v-if="contact.sms_on_low_battery" class="text-xs px-1.5 py-0.5 bg-yellow-50 text-yellow-500 rounded">{{ $t('contacts.smsBatterieFaible') }}</span>
              <span v-if="contact.sms_on_offline"     class="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{{ $t('contacts.smsHorsLigne') }}</span>
              <span v-if="aucuneNotification(contact)" class="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                {{ $t('contacts.aucuneNotification') }}
              </span>
            </div>
          </div>
          <div v-if="modifiable" class="flex gap-3 whitespace-nowrap">
            <button @click="ouvrirEdition(contact)" class="text-xs text-primary-600 hover:underline">{{ $t('contacts.modifier') }}</button>
            <button @click="supprimer(contact)" class="text-xs text-red-500 hover:underline">{{ $t('contacts.supprimer') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Formulaire, en ajout comme en modification ─────────────────────────────────── -->
    <div v-if="modale" class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4 py-8">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="fermer"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 class="text-lg font-medium mb-4">
            {{ enEdition ? $t('contacts.titreModifier') : $t('contacts.titreAjouter') }}
          </h3>

          <div v-if="erreur" class="mb-3 p-2 bg-red-50 text-red-600 text-sm rounded">{{ erreur }}</div>

          <div class="space-y-3">
            <div>
              <label class="form-label">{{ $t('contacts.nom') }}</label>
              <input type="text" v-model="form.name" class="form-control" :placeholder="$t('contacts.exempleNom')">
            </div>
            <div>
              <label class="form-label">{{ $t('contacts.telephone') }}</label>
              <input type="tel" v-model="form.phone_number" class="form-control" placeholder="06 12 34 56 78">
              <!-- Le serveur préfixe en +33 tout numéro commençant par 0 (twilio.service.js).
                   Cette hypothèse est invisible à la saisie : on la MONTRE, plutôt que de
                   laisser découvrir qu'un numéro étranger mal saisi n'a jamais été appelé. -->
              <i18n-t v-if="numeroNormalise" keypath="contacts.formatInternational" tag="p" class="mt-1 text-xs text-gray-500">
                <template #numero><strong>{{ numeroNormalise }}</strong></template>
              </i18n-t>
              <p v-else-if="form.phone_number" class="mt-1 text-xs text-red-600">
                {{ $t('contacts.numeroNonReconnu') }}
              </p>
            </div>

            <div class="pt-2 border-t border-gray-100">
              <p class="text-xs text-gray-500 mb-2">{{ $t('contacts.ceQueRecoit') }}</p>
              <div class="grid grid-cols-2 gap-2">
                <label class="flex items-center gap-2 text-sm">
                  <input type="checkbox" v-model="form.call_on_alarm" class="rounded"> {{ $t('contacts.appelAlarme') }}
                </label>
                <label class="flex items-center gap-2 text-sm">
                  <input type="checkbox" v-model="form.sms_on_alarm" class="rounded"> {{ $t('contacts.smsAlarme') }}
                </label>
                <label class="flex items-center gap-2 text-sm">
                  <input type="checkbox" v-model="form.sms_on_tamper" class="rounded"> {{ $t('contacts.smsSabotage') }}
                </label>
                <label class="flex items-center gap-2 text-sm">
                  <input type="checkbox" v-model="form.sms_on_power_loss" class="rounded"> {{ $t('contacts.smsCoupureSecteur') }}
                </label>
                <label class="flex items-center gap-2 text-sm">
                  <input type="checkbox" v-model="form.sms_on_low_battery" class="rounded"> {{ $t('contacts.smsBatterieFaible') }}
                </label>
                <label class="flex items-center gap-2 text-sm">
                  <input type="checkbox" v-model="form.sms_on_offline" class="rounded"> {{ $t('contacts.smsHorsLigne') }}
                </label>
              </div>
              <div class="mt-3">
                <label class="form-label">{{ $t('contacts.langue') }}</label>
                <select v-model="form.lang" class="form-control">
                  <option value="">{{ $t('contacts.langueTitulaire') }}</option>
                  <option v-for="l in langues" :key="l.code" :value="l.code">{{ l.nom }}</option>
                </select>
                <p class="mt-1 text-xs text-gray-500">{{ $t('contacts.langueAide') }}</p>
              </div>
              <p v-if="aucuneNotification(form)" class="mt-2 text-xs text-amber-700">
                {{ $t('contacts.aucuneCase') }}
              </p>
            </div>
          </div>

          <div class="mt-5 flex gap-2 justify-end">
            <button @click="fermer" class="btn btn-secondary">{{ $t('commun.annuler') }}</button>
            <button @click="enregistrer" :disabled="envoi" class="btn btn-primary">
              {{ envoi ? $t('contacts.enregistrement') : $t('commun.enregistrer') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation de suppression, en modale plutôt qu'en `confirm()` natif : le reste de
         l'application ne l'utilise plus, et il est purement et simplement ignoré dans
         certains contextes. -->
    <div v-if="aSupprimer" class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="aSupprimer = null"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 class="text-lg font-medium">{{ $t('contacts.supprimerTitre', { nom: aSupprimer.name }) }}</h3>
          <p class="mt-2 text-sm text-gray-600">
            {{ $t('contacts.suppressionDetail') }}
            <template v-if="contacts.length === 1">
              <strong class="text-amber-700">{{ $t('contacts.dernier') }}</strong>
            </template>
          </p>
          <div class="mt-5 flex gap-2 justify-end">
            <button @click="aSupprimer = null" class="btn btn-secondary">{{ $t('commun.annuler') }}</button>
            <button @click="confirmerSuppression" :disabled="envoi" class="btn btn-primary bg-red-600 hover:bg-red-700">
              {{ $t('contacts.supprimer') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { texteErreur } from '@/i18n/serveur';
import { LANGUES } from '@/i18n';

const parDefaut = () => ({
  name: '', phone_number: '',
  call_on_alarm: true, sms_on_alarm: true,
  sms_on_tamper: true, sms_on_power_loss: true,
  sms_on_low_battery: true, sms_on_offline: true,
  lang: ''
});

const CASES = ['call_on_alarm', 'sms_on_alarm', 'sms_on_tamper',
               'sms_on_power_loss', 'sms_on_low_battery', 'sms_on_offline'];

// Reproduit `formatPhoneNumber` de backend/services/twilio.service.js : « + » conservé,
// « 0 » initial remplacé par « +33 ». On le refait ici pour MONTRER le résultat avant
// d'enregistrer, pas pour transformer la donnée — c'est le serveur qui convertit à l'envoi.
function normaliser(saisi) {
  const net = String(saisi || '').replace(/[\s.\-()]/g, '');
  if (/^\+\d{8,15}$/.test(net)) return net;
  if (/^0\d{9}$/.test(net))     return '+33' + net.slice(1);
  return null;
}

export default defineComponent({
  name: 'ContactsPanel',

  props: {
    deviceId: { type: [Number, String], required: true },
    contacts: { type: Array, default: () => [] },
    // Comptes partagés : un membre consulte les contacts d'alerte, seul le titulaire les modifie.
    modifiable: { type: Boolean, default: true }
  },

  // La liste reste la propriété du parent, qui l'a chargée avec l'appareil. On lui renvoie
  // la version à jour plutôt que de muter sa donnée dans son dos.
  emits: ['maj'],

  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const modale = ref(false), enEdition = ref(null);
    const langues = LANGUES;
    const nomLangue = code => (LANGUES.find(l => l.code === code) || {}).nom || code;
    const form = ref(parDefaut());
    const erreur = ref(''), envoi = ref(false);
    const aSupprimer = ref(null);

    const numeroNormalise = computed(() => normaliser(form.value.phone_number));
    const aucuneNotification = c => CASES.every(k => !c[k]);

    const ouvrirAjout = () => {
      enEdition.value = null; form.value = parDefaut(); erreur.value = ''; modale.value = true;
    };
    const ouvrirEdition = (c) => {
      enEdition.value = c.id; form.value = { ...c, lang: c.lang || '' }; erreur.value = ''; modale.value = true;
    };
    const fermer = () => { modale.value = false; enEdition.value = null; erreur.value = ''; };

    const enregistrer = async () => {
      if (!form.value.name?.trim()) { erreur.value = t('contacts.nomRequis'); return; }
      if (!numeroNormalise.value) {
        erreur.value = t('contacts.numeroInvalide');
        return;
      }
      erreur.value = ''; envoi.value = true;
      try {
        // Les espaces sont retirés, mais on NE convertit PAS le 0 en +33 avant d'enregistrer :
        // la conversion appartient au serveur, et la dupliquer ici ferait deux endroits à
        // corriger le jour où le parc sort de France.
        const donnees = { ...form.value, phone_number: String(form.value.phone_number).replace(/[\s.\-()]/g, '') };

        if (enEdition.value) {
          await store.dispatch('device/updateContact', {
            deviceId: props.deviceId, contactId: enEdition.value, contactData: donnees
          });
          emit('maj', props.contacts.map(c => (c.id === enEdition.value ? { ...c, ...donnees } : c)));
        } else {
          const r = await store.dispatch('device/addContact', {
            deviceId: props.deviceId, contactData: donnees
          });
          emit('maj', [...props.contacts, { id: r.contactId, ...donnees }]);
        }
        fermer();
      } catch (e) {
        erreur.value = texteErreur(e, t('contacts.enregistrementImpossible'));
      } finally {
        envoi.value = false;
      }
    };

    const supprimer = (c) => { aSupprimer.value = c; };

    const confirmerSuppression = async () => {
      envoi.value = true;
      try {
        await store.dispatch('device/deleteContact', {
          deviceId: props.deviceId, contactId: aSupprimer.value.id
        });
        emit('maj', props.contacts.filter(c => c.id !== aSupprimer.value.id));
        aSupprimer.value = null;
      } catch (e) {
        erreur.value = texteErreur(e, t('contacts.suppressionImpossible'));
      } finally {
        envoi.value = false;
      }
    };

    return {
      langues, nomLangue, modale, enEdition, form, erreur, envoi, aSupprimer, numeroNormalise,
             aucuneNotification, ouvrirAjout, ouvrirEdition, fermer, enregistrer,
             supprimer, confirmerSuppression };
  }
});
</script>
